
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Rental, Bike, Courier } from '@/services/types';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/sonner';

interface EditRentalFormProps {
  rental: Rental;
  bikes: Bike[];
  couriers: Courier[];
  loading: boolean;
  onSubmit: (data: any) => Promise<boolean>;
}

export const EditRentalForm: React.FC<EditRentalFormProps> = ({
  rental,
  bikes,
  couriers,
  loading: externalLoading,
  onSubmit
}) => {
  // Состояния для полей формы
  const [bikeId, setBikeId] = useState<string>(rental.bike_id || rental.bikeId || '');
  const [courierId, setCourierId] = useState<string>(rental.courier_id || rental.courierId || '');
  const [cost, setCost] = useState<number>(rental.cost || rental.amount || 0);
  const [status, setStatus] = useState<Rental['status']>(rental.status);
  const [startDate, setStartDate] = useState<Date>(new Date(rental.start_date || rental.startDate || Date.now()));
  const [endDate, setEndDate] = useState<Date | undefined>(
    rental.end_date || rental.endDate ? new Date(rental.end_date || rental.endDate || '') : undefined
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  
  // Внутреннее состояние загрузки, не зависящее от внешнего
  const [localLoading, setLocalLoading] = useState(false);
  
  // Синхронизируем данные при изменении rental, но только один раз при инициализации
  useEffect(() => {
    setBikeId(rental.bike_id || rental.bikeId || '');
    setCourierId(rental.courier_id || rental.courierId || '');
    setCost(rental.cost || rental.amount || 0);
    setStatus(rental.status);
    setStartDate(new Date(rental.start_date || rental.startDate || Date.now()));
    setEndDate(rental.end_date || rental.endDate ? new Date(rental.end_date || rental.endDate || '') : undefined);
  }, [rental.id]); // Зависимость только от ID, а не от всей rental

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    
    if (!bikeId || !courierId) {
      setFormError("Пожалуйста, заполните все обязательные поля");
      toast.error("Пожалуйста, заполните все обязательные поля");
      return;
    }
    
    try {
      setIsSubmitting(true);
      setLocalLoading(true);
      
      // Преобразуем данные в формат snake_case для API
      const success = await onSubmit({
        bike_id: bikeId,
        courier_id: courierId,
        amount: cost,
        status,
        start_date: startDate.toISOString(),
        end_date: endDate ? endDate.toISOString() : null
      });
      
      if (success) {
        toast.success("Аренда успешно обновлена");
      }
    } catch (error: any) {
      setFormError(error.message || 'Неизвестная ошибка');
      toast.error(`Ошибка при сохранении: ${error.message || 'Неизвестная ошибка'}`);
      console.error('Ошибка при отправке формы:', error);
    } finally {
      // Независимо от результата, сбрасываем локальное состояние загрузки
      setIsSubmitting(false);
      setLocalLoading(false);
    }
  };
  
  // Используем для отображения состояния загрузки только локальное состояние
  const isLoadingState = isSubmitting || localLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {formError && (
        <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
          {formError}
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="bikeId">Велосипед</Label>
          <Select value={bikeId} onValueChange={setBikeId} disabled={isLoadingState}>
            <SelectTrigger>
              <SelectValue placeholder="Выберите велосипед" />
            </SelectTrigger>
            <SelectContent>
              {bikes.map((bike) => (
                <SelectItem key={bike.id} value={bike.id}>
                  {bike.model} ({bike.serialNumber})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="courierId">Курьер</Label>
          <Select value={courierId} onValueChange={setCourierId} disabled={isLoadingState}>
            <SelectTrigger>
              <SelectValue placeholder="Выберите курьера" />
            </SelectTrigger>
            <SelectContent>
              {couriers.map((courier) => (
                <SelectItem key={courier.id} value={courier.id}>
                  {courier.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="cost">Стоимость (₽)</Label>
          <Input
            id="cost"
            type="number"
            value={cost}
            onChange={(e) => setCost(parseFloat(e.target.value) || 0)}
            disabled={isLoadingState}
          />
        </div>
        
        <div>
          <Label htmlFor="status">Статус</Label>
          <Select value={status} onValueChange={(value: Rental['status']) => setStatus(value)} disabled={isLoadingState}>
            <SelectTrigger>
              <SelectValue placeholder="Выберите статус" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Активна</SelectItem>
              <SelectItem value="completed">Завершена</SelectItem>
              <SelectItem value="cancelled">Отменена</SelectItem>
              <SelectItem value="pending">Ожидает</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Дата начала</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !startDate && "text-muted-foreground"
                )}
                disabled={isLoadingState}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, 'PPP', { locale: ru }) : <span>Выберите дату</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => setStartDate(date || new Date())}
                initialFocus
                locale={ru}
              />
            </PopoverContent>
          </Popover>
        </div>
        
        <div>
          <Label>Дата окончания</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !endDate && "text-muted-foreground"
                )}
                disabled={isLoadingState}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, 'PPP', { locale: ru }) : <span>Выберите дату</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={setEndDate}
                initialFocus
                locale={ru}
                disabled={date => date < startDate}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="submit" disabled={isLoadingState}>
          {isLoadingState ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Сохранение...
            </>
          ) : 'Сохранить'}
        </Button>
      </div>
    </form>
  );
};
